import { removeItemAsync, updateItemAsync, updateCountItemsInCart } from '@src/api/cart';
import { CartItemModel, CartModel } from '@src/app/types';
import Button from '@src/components/button/Button';
import CartItem from '@src/components/cart-item/CartItem';
import Svg from '@src/components/svg/Svg';
import { useEffect, useRef, useState } from 'react';

interface Props extends CartModel {
  accountId?: string;
  locationId?: string;
  addedToCartText: string;
  viewCartText: string;
  viewCartUrl: string;
  checkoutText: string;
  checkoutUrl: string;
  vatText: string;
  totalText: string;
  onClose: () => any;
}

let miniCartKey = 0;

const MiniCart = ({
  addedToCartText,
  checkoutText,
  checkoutUrl,
  totalText,
  vatText,
  viewCartText,
  viewCartUrl,
  accountId,
  locationId,
  onClose,
  ...props
}: Props) => {
  const [closeClicked, setCloseClicked] = useState(false);
  const [cart, setCart] = useState<CartModel>(props);
  const [isApiCalling, setIsApiCalling] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const className = 'fm-o-mini-cart--popup-visible';
    document.body.classList.add(className);

    return () => {
      document.body.classList.remove(className);
    };
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setCloseClicked(true);
    }
  };

  const handleClose = () => {
    setCloseClicked(true);
  };

  const handleAnimationEnd = () => {
    if (!closeClicked) {
      return;
    }

    onClose();
  };

  const handlePushDataLayer = (item: CartItemModel, eventName: string, quantity: number) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        currencyCode: 'GBP',
        add: {
          products: [
            {
              name: item.name,
              variant: item.description,
              price: item.priceIncludedTax,
              quantity,
              id: item.productId,
            },
          ],
        },
      },
    });
  };

  const handleRemoveFromCart = (item: CartItemModel) => {
    setIsApiCalling(true);
    removeItemAsync(item.itemId)
      .then(() => {
        updateCountItemsInCart();
        handlePushDataLayer(item, 'removeFromCart', item.quantity);
        setCloseClicked(true);
      })
      .finally(() => setIsApiCalling(false));
  };

  const handleSetQuantity = (item: CartItemModel, quantity: number) => {
    setIsApiCalling(true);
    const oldItems = cart.items;
    const newItems = cart.items?.map((i) => (i.itemId === item.itemId ? { ...i, quantity: quantity } : i));
    const eventName = item.quantity > quantity ? 'removeFromCart' : 'addToCart';

    setCart({ ...cart, items: newItems });
    updateItemAsync(item.itemId, { quantity: quantity, accountId, locationId, quantityRemaining: item.quantityRemaining })
      .then((data: CartModel) => {
        const newItem = data.items?.filter((i) => i.itemId === item.itemId);
        newItem && handlePushDataLayer(newItem[0], eventName, Math.abs(item.quantity - quantity));
        setCart(data as CartModel);
      })
      .catch(() => {
        setCart({ ...cart, items: oldItems });
      })
      .finally(() => setIsApiCalling(false));
  };

  return (
    <section className={'fm-o-mini-cart fm-o-mini-cart--' + (closeClicked ? 'hide' : 'show')} onAnimationEnd={handleAnimationEnd}>
      <div ref={modalRef} className={'fm-o-mini-cart__container fm-o-mini-cart__container--' + (closeClicked ? 'hide' : 'show')}>
        <div className="fm-o-mini-cart__inner">
          <div className="fm-o-mini-cart__title-bar">
            <h2 className="fm-o-mini-cart__title tfm">{addedToCartText}</h2>
            <div className="fm-o-mini-cart__close" onClick={handleClose}>
              <Svg mainClass="fm-o-mini-cart__close-icon" iconPath="#cross-2" viewBoxWidth={13} viewBoxHeight={13} />
            </div>
          </div>
          <div className="fm-o-mini-cart__items">
            {cart.items?.map((item) => (
              <CartItem
                key={++miniCartKey}
                {...item}
                onRemoveFromCart={() => handleRemoveFromCart(item)}
                onSetQuantity={(quantity) => handleSetQuantity(item, quantity)}
              />
            ))}
          </div>
          <div className="fm-o-mini-cart__props">
            <div className="fm-o-mini-cart__prop-name">{vatText}</div>
            <div className="fm-o-mini-cart__prop-value">{cart.vat}</div>
            <div className="fm-o-mini-cart__prop-name">{totalText}</div>
            <div className="fm-o-mini-cart__prop-value fm-o-mini-cart__total-price">{cart.totalPrice}</div>
          </div>
          <div className="fm-o-mini-cart__cta">
            <Button
              text={viewCartText}
              raw={true}
              link={true}
              url={viewCartUrl}
              mainClass="fm-o-mini-cart__cta-view-cart"
              styleModifier={['fm-a-button--dark', 'fm-a-button--small-x-padding']}
            />
            <Button
              text={checkoutText}
              raw={true}
              link={true}
              url={checkoutUrl}
              mainClass="fm-o-mini-cart__cta-checkout"
              styleModifier={['fm-a-button--blue', 'fm-a-button--small-x-padding']}
            />
          </div>
        </div>
        {isApiCalling && <div className="fm-o-mini-cart__loading"></div>}
      </div>
    </section>
  );
};

export default MiniCart;
export type { Props as MiniCartProps };
